import { useEffect, useRef } from 'react';

import { useAnalytics } from 'hooks/useAnalytics';
import { ZoomChatAnalyticsEvents } from 'constants/analyticsEvents';

import { reloadToEvictZoomChatSdk } from './clearZoomChatIdentity';
import { useZoomChatBubbleVisibility } from './useZoomChatBubbleVisibility';
import {
  useZoomChatAllowed,
  useZoomChatEnabled,
  useZoomChatVisibility
} from './useZoomChatVisibility';
import ZoomChatWidget from './ZoomChatWidget';

/**
 * Mounted in App beside the router and outside the keyed route tree, so it is not rebuilt on every
 * page change. Two answers are kept apart on purpose. `allowed` decides whether the chat exists at
 * all (flags, signed-in patient, not the mobile app, not staff viewing as the patient): the widget,
 * and the identity hook it registers, are mounted while it holds and stay mounted across ordinary
 * page changes. App's loader and a session change can still remount them, and the identity watchdog
 * is armed by the SDK loader inside the widget, so it starts only once the script is injected or
 * already in the document. `visible` is `allowed` plus the page not being on the hidden list, and it
 * decides only what the patient sees: the bubble, through useZoomChatBubbleVisibility, and the
 * impression event, which is logged once per mount of this component, so a remount can log it again.
 */
const ZoomChatSlot = () => {
  const allowed = useZoomChatAllowed();
  const visible = useZoomChatVisibility();
  const isEnabled = useZoomChatEnabled();
  const logEvent = useAnalytics();
  const impressedRef = useRef(false);
  const wasEnabledRef = useRef(isEnabled);

  useZoomChatBubbleVisibility(visible);

  useEffect(() => {
    if (visible && !impressedRef.current) {
      impressedRef.current = true;
      logEvent(ZoomChatAnalyticsEvents.WidgetImpression);
    }
  }, [visible, logEvent]);

  /**
   * Makes the flag an actual kill switch rather than only a startup gate.
   *
   * Unmounting removes our host hook, so no further identity is minted, but it cannot unload an
   * executed ES module: a tab that already rendered Zoom's bubble would keep it, and keep taking
   * whatever the patient types, until they happened to navigate. On a PHI-capable vendor surface, a
   * switch that only takes effect on the next page load is not a switch.
   *
   * Keyed on the FLAG, not on `allowed` or `visible`, and only on the true-to-false edge. They also
   * drop on route changes and sign-out, where a reload would be wrong or is already handled; the
   * flag changes only when someone turns the channel off, which is exactly when eviction is wanted.
   * It acts wherever Zoom's script is loaded, on any page, and does nothing on a document that
   * never loaded it. Unleash polls every 60s, so switching it off lands within about a minute.
   */
  useEffect(() => {
    if (wasEnabledRef.current && !isEnabled) {
      reloadToEvictZoomChatSdk({ inPlace: true });
    }

    wasEnabledRef.current = isEnabled;
  }, [isEnabled]);

  if (!allowed) return null;

  return <ZoomChatWidget />;
};

export default ZoomChatSlot;
